import { useEffect, useState } from "react";
import { Building2, Sparkles, MapPin, TrendingUp, ShieldCheck, FileSearch } from "lucide-react";
import api from "../lib/api";
import { formatPct, formatEur, formatDateTime } from "../lib/format";
import { KpiCard } from "../components/KpiCard";
import EmptyState from "../components/EmptyState";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import ZoneBadge from "../components/ZoneBadge";
import { FlipScoreGauge } from "../components/FlipScoreGauge";

const Overview = () => {
    const [overview, setOverview] = useState(null);
    const [integrity, setIntegrity] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = () => {
            Promise.all([api.get("/overview"), api.get("/integrity")])
                .then(([o, i]) => {
                    setOverview(o.data);
                    setIntegrity(i.data);
                })
                .finally(() => setLoading(false));
        };
        load();
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
    }, []);

    if (loading) {
        return (
            <div className="space-y-6" data-testid="overview-loading">
                <Skeleton className="h-44 w-full" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-36" />
                    ))}
                </div>
            </div>
        );
    }

    const hasData = (overview?.total_listings || 0) > 0;

    return (
        <div className="space-y-8" data-testid="overview-page">
            {/* Hero header */}
            <section className="relative overflow-hidden rounded-lg border border-border bg-card">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="relative grid md:grid-cols-[1fr_auto] gap-6 p-6 md:p-8 items-end">
                    <div>
                        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05] text-foreground">
                            Flip Finder
                            <span className="text-accent"> · Montenegro</span>
                        </h1>
                        <p className="text-sm md:text-base text-muted-foreground mt-4 max-w-2xl leading-relaxed">
                            Professional deal sourcing and investment analysis for
                            residential flip opportunities along Montenegro's Adriatic coast.
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Button asChild variant="outline" data-testid="overview-cta-sources">
                            <Link to="/sources">Manage sources</Link>
                        </Button>
                        <Button asChild data-testid="overview-cta-deals">
                            <Link to="/deals">Open Deal Finder</Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* KPI Bento */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    label="Listings scanned"
                    value={overview?.total_listings ?? 0}
                    sub={
                        hasData
                            ? `Across ${integrity?.sources_connected ?? 0} connected sources`
                            : "No source connected yet"
                    }
                    icon={Building2}
                    testid="kpi-listings"
                />
                <KpiCard
                    label="New this week"
                    value={overview?.new_this_week ?? 0}
                    sub={hasData ? "Within rolling 7-day window" : "Awaiting first scan"}
                    icon={Sparkles}
                    testid="kpi-new-week"
                />
                <KpiCard
                    label="Strong candidates"
                    value={overview?.strong_candidates ?? 0}
                    sub={hasData ? "Flip score ≥ 70" : "Score engine ready"}
                    icon={ShieldCheck}
                    testid="kpi-strong"
                />
                <KpiCard
                    label="Avg expected ROI"
                    value={
                        overview?.avg_expected_roi_pct === null ||
                        overview?.avg_expected_roi_pct === undefined
                            ? "—"
                            : formatPct(overview.avg_expected_roi_pct)
                    }
                    sub={
                        overview?.best_area_this_week
                            ? `Hot area: ${overview.best_area_this_week}`
                            : "Insufficient data to compute"
                    }
                    icon={TrendingUp}
                    testid="kpi-roi"
                    accent
                />
            </section>

            {/* Integrity panel */}
            <section>
                <Card className="p-6" data-testid="integrity-card">
                    <div className="flex items-start justify-between gap-3 mb-5">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                                Real-data integrity check
                            </div>
                            <h2 className="font-grotesk font-semibold text-lg mt-1">
                                What the dashboard is actually working with
                            </h2>
                        </div>
                        <FileSearch className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        <Metric
                            label="Total real listings"
                            value={integrity?.total_listings ?? 0}
                            testid="integrity-listings"
                        />
                        <Metric
                            label="Verified from source"
                            value={integrity?.verified_listings ?? 0}
                            testid="integrity-verified"
                        />
                        <Metric
                            label="Sources connected"
                            value={`${integrity?.sources_connected ?? 0}/${
                                integrity?.sources_total ?? 0
                            }`}
                            testid="integrity-sources"
                        />
                        <Metric
                            label="Provenance coverage"
                            value={
                                integrity?.pct_with_provenance
                                    ? `${integrity.pct_with_provenance}%`
                                    : "—"
                            }
                            testid="integrity-prov"
                        />
                        <Metric
                            label="Missing price"
                            value={integrity?.listings_missing_price ?? 0}
                            testid="integrity-missing-price"
                        />
                        <Metric
                            label="Missing m²"
                            value={integrity?.listings_missing_area ?? 0}
                            testid="integrity-missing-area"
                        />
                        <Metric
                            label="Missing municipality"
                            value={integrity?.listings_missing_municipality ?? 0}
                            testid="integrity-missing-muni"
                        />
                        <Metric
                            label="Blocked calculations"
                            value={integrity?.blocked_calculations ?? 0}
                            testid="integrity-blocked"
                        />
                        <Metric
                            label="Last successful scan"
                            value={formatDateTime(integrity?.last_successful_scan_at)}
                            small
                            testid="integrity-last-success"
                        />
                        <Metric
                            label="Last failed scan"
                            value={formatDateTime(integrity?.last_failed_scan_at)}
                            small
                            testid="integrity-last-fail"
                        />
                        <Metric
                            label="Sources failed"
                            value={integrity?.sources_failed ?? 0}
                            small
                            testid="integrity-failed"
                        />
                        <Metric
                            label="Next scheduled scan"
                            value={formatDateTime(integrity?.next_scheduled_scan_at)}
                            small
                            testid="integrity-next-scan"
                        />
                    </div>
                </Card>
            </section>

            {/* Top 5 */}
            <section>
                <div className="flex items-end justify-between mb-4">
                    <div>
                        <h2 className="font-grotesk font-semibold text-xl">
                            Top 5 opportunities
                        </h2>
                        <p className="text-xs text-muted-foreground">
                            Highest Flip Score across connected sources
                        </p>
                    </div>
                    <Button asChild variant="ghost" size="sm" data-testid="overview-all-deals">
                        <Link to="/deals">All deals →</Link>
                    </Button>
                </div>
                {overview?.top5?.length ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {overview.top5.map((l) => (
                            <Link
                                to={`/deals/${l.id}`}
                                key={l.id}
                                data-testid={`top5-card-${l.id}`}
                            >
                                <Card className="p-5 hover:-translate-y-0.5 transition-transform h-full">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="font-grotesk font-medium text-sm line-clamp-1">
                                                {l.title || "Untitled listing"}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                                                <MapPin className="w-3 h-3" />
                                                {l.municipality || "—"}
                                            </div>
                                        </div>
                                        <ZoneBadge zone={l.zone_class} />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <FlipScoreGauge
                                            score={l.flip_score}
                                            label={l.flip_label}
                                            size={88}
                                        />
                                        <div className="space-y-2 text-xs flex-1">
                                            <Row label="Asking" value={formatEur(l.asking_price_eur)} />
                                            <Row
                                                label="€ / m²"
                                                value={
                                                    l.price_per_sqm_eur
                                                        ? formatEur(l.price_per_sqm_eur)
                                                        : "—"
                                                }
                                            />
                                            <Row
                                                label="Sea view"
                                                value={l.sea_view === "unknown" ? "—" : l.sea_view}
                                            />
                                        </div>
                                    </div>
                                </Card>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <EmptyState
                        title="Geen echte deals beschikbaar"
                        description="Zodra een scraper succesvol echte listings ophaalt verschijnen hier de top 5 kandidaten op basis van Flip Score, locatie en zeezicht."
                        action={
                            <Button asChild data-testid="overview-empty-cta">
                                <Link to="/sources">Configure data sources</Link>
                            </Button>
                        }
                        testid="overview-top5-empty"
                    />
                )}
            </section>
        </div>
    );
};

const Metric = ({ label, value, small, testid }) => (
    <div data-testid={testid}>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
            {label}
        </div>
        <div
            className={
                small
                    ? "text-sm font-grotesk font-medium"
                    : "kpi-value text-3xl text-foreground"
            }
        >
            {value}
        </div>
    </div>
);

const Row = ({ label, value }) => (
    <div className="flex items-baseline justify-between gap-3">
        <span className="text-muted-foreground text-[11px] uppercase tracking-wider">
            {label}
        </span>
        <span className="font-medium text-foreground">{value}</span>
    </div>
);

export default Overview;
